import React, { useState } from 'react';

import { useGoogleLogin } from '@react-oauth/google';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';

import { useAuth } from 'contexts/AuthContext';

import { TotemInput } from 'components/TotemInput';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import { setTokens } from 'data/connection/storage';
import { useSigninTotemAdminMutation, useSigninTotemAdminSsoMutation } from 'data/__generated__';

export const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [signIn, { loading: signInLoading }] = useSigninTotemAdminMutation();
    const [signInSSO, { loading: signInSSOLoading }] = useSigninTotemAdminSsoMutation();
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

    const handleLoginSuccess = (
        token: string,
        refreshToken: string | undefined,
        user: Parameters<typeof setUser>[0],
    ) => {
        if (!user) return;
        setTokens(token, refreshToken);
        setUser(user);
        navigate(from, { replace: true });
    };

    const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);

        const { data } = await signIn({ variables: { username, password } });
        const success = data?.signinTotemAdmin?.success;
        const token = data?.signinTotemAdmin?.token;
        const refreshToken = data?.signinTotemAdmin?.refreshToken;
        const user = data?.signinTotemAdmin?.user;
        const signInError = data?.signinTotemAdmin?.error;

        if (success && token && user) {
            handleLoginSuccess(token, refreshToken ?? undefined, user);
        } else {
            setError(signInError || null);
        }
    };

    const googleLogin = useGoogleLogin({
        flow: 'auth-code',
        onSuccess: async (codeResponse) => {
            setError(null);
            const { data } = await signInSSO({ variables: { code: codeResponse.code } });
            const success = data?.signinTotemAdminSSO.success;
            const signInSSOError = data?.signinTotemAdminSSO?.error;
            const token = data?.signinTotemAdminSSO?.token;
            const refreshToken = data?.signinTotemAdminSSO?.refreshToken;
            const user = data?.signinTotemAdminSSO?.user;

            if (success && token && user) {
                handleLoginSuccess(token, refreshToken ?? undefined, user);
            } else {
                setError(signInSSOError || null);
            }
        },
        onError: () => {
            setError('Une erreur est survenue lors de la connexion avec Google');
        },
    });

    return (
        <Container>
            <Helmet title="Connexion · TOTADMIN" />
            <Title>Connexion</Title>
            <LoginForm onSubmit={submitForm}>
                <TotemInput
                    value={username}
                    placeholder="Adresse email"
                    onChange={(value) => setUsername(value)}
                    dataTest="login-username"
                />
                <TotemInput
                    value={password}
                    placeholder="Mot de passe"
                    type="password"
                    onChange={(value) => setPassword(value)}
                    dataTest="login-password"
                />
                <ForgotPasswordLink to="/recover-password">Mot de passe oublié ?</ForgotPasswordLink>
                {error ? <ErrorMessage>L'erreur suivante est survenue : {error}</ErrorMessage> : null}
                <SubmitButton type="submit" data-test="login-submit" disabled={signInLoading || signInSSOLoading}>
                    Se connecter
                </SubmitButton>
            </LoginForm>
            <SeparatorContainer>
                <SeparatorLine />
                <SeparatorText>ou</SeparatorText>
                <SeparatorLine />
            </SeparatorContainer>
            <GoogleButton type="button" onClick={() => googleLogin()} data-test="login-google">
                <GoogleLogo viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                        fill="#4285F4"
                    />
                    <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                    />
                    <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                    />
                    <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                    />
                </GoogleLogo>
                Se connecter avec Google
            </GoogleButton>
        </Container>
    );
};

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ForgotPasswordLink = styled(Link)`
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
`;

const ErrorMessage = styled.span`
    color: ${({ theme }) => theme.errorColor};
`;

const Title = styled.h1`
    text-transform: uppercase;
    color: ${({ theme }) => theme.textColor};
    letter-spacing: 0.7px;
    font-size: 40px;
`;

const LoginForm = styled.form`
    display: flex;
    flex-direction: column;
    width: 400px;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const SubmitButton = styled(TotemPrimaryButton)`
    margin-top: 15px;
`;

const SeparatorContainer = styled.div`
    display: flex;
    align-items: center;
    width: 250px;
    margin: 20px 0;
`;

const SeparatorLine = styled.div`
    flex: 1;
    height: 1px;
    background-color: ${({ theme }) => theme.textColor}33;
`;

const SeparatorText = styled.span`
    padding: 0 12px;
    color: ${({ theme }) => theme.textColor};
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 1px;
`;

const GoogleLogo = styled.svg`
    width: 20px;
    height: 20px;
    flex-shrink: 0;
`;

const GoogleButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 250px;
    padding: 12px 16px;
    background-color: #fff;
    color: #3c4043;
    border: 1px solid #dadce0;
    border-radius: ${({ theme }) => theme.borderRadius};
    font-size: 14px;
    font-weight: 500;
    font-family: 'Roboto', Arial, sans-serif;
    cursor: pointer;
    transition:
        background-color 0.2s,
        box-shadow 0.2s;

    &:hover {
        background-color: #f7f8f8;
        box-shadow: 0 1px 3px rgba(60, 64, 67, 0.3);
    }

    &:active {
        background-color: #eee;
    }
`;
